import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowUpRight, Loader2 } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Overline } from "./Motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = { name: "", email: "", phone: "", company: "", role: "", interest: "", message: "" };

export const useQuoteForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (onDone) => {
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please add your name and email.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, form);
      toast.success("Thank you — our team will reach out shortly.");
      setForm(EMPTY);
      onDone && onDone();
    } catch (e) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return { form, set, submit, loading };
};

const fieldCls =
  "w-full bg-transparent border-b-2 border-black/10 focus:border-royal outline-none py-3 text-royal-deep placeholder:text-muted-foreground/60 transition-colors duration-300";

export const QuoteFields = ({ form, set, dark = false }) => {
  const border = dark ? "border-white/20 focus:border-white text-white placeholder:text-white/40" : "border-black/10 focus:border-royal text-royal-deep";
  const base = `w-full bg-transparent border-b-2 outline-none py-3 transition-colors duration-300 ${border}`;
  return (
    <div className="grid sm:grid-cols-2 gap-x-8 gap-y-6">
      <input data-testid="lead-name" className={base} placeholder="Full name *" value={form.name} onChange={(e) => set("name", e.target.value)} />
      <input data-testid="lead-email" type="email" className={base} placeholder="Email *" value={form.email} onChange={(e) => set("email", e.target.value)} />
      <input data-testid="lead-phone" className={base} placeholder="Phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
      <input data-testid="lead-company" className={base} placeholder="Company / Practice" value={form.company} onChange={(e) => set("company", e.target.value)} />

      <Select value={form.role} onValueChange={(v) => set("role", v)}>
        <SelectTrigger data-testid="lead-role" className={`rounded-none border-0 border-b-2 bg-transparent px-0 py-3 h-auto ${dark ? "border-white/20 text-white" : "border-black/10 text-royal-deep"} focus:ring-0 focus:border-royal`}>
          <SelectValue placeholder="I am a..." />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="architect">Architect</SelectItem>
          <SelectItem value="builder">Builder / Contractor</SelectItem>
          <SelectItem value="interior-designer">Interior Designer</SelectItem>
          <SelectItem value="homeowner">Homeowner</SelectItem>
          <SelectItem value="other">Other</SelectItem>
        </SelectContent>
      </Select>

      <Select value={form.interest} onValueChange={(v) => set("interest", v)}>
        <SelectTrigger data-testid="lead-interest" className={`rounded-none border-0 border-b-2 bg-transparent px-0 py-3 h-auto ${dark ? "border-white/20 text-white" : "border-black/10 text-royal-deep"} focus:ring-0 focus:border-royal`}>
          <SelectValue placeholder="Interested in..." />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="flooring">Flooring</SelectItem>
          <SelectItem value="ceiling">Ceiling</SelectItem>
          <SelectItem value="facade">Terracotta Facade</SelectItem>
          <SelectItem value="all">All verticals</SelectItem>
        </SelectContent>
      </Select>

      <div className="sm:col-span-2">
        <textarea data-testid="lead-message" rows={3} className={base} placeholder="Tell us about your project" value={form.message} onChange={(e) => set("message", e.target.value)} />
      </div>
    </div>
  );
};

export const Contact = () => {
  const { form, set, submit, loading } = useQuoteForm();

  return (
    <section id="contact" className="bg-white py-24 lg:py-36">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-5">
          <Overline>Request a quote</Overline>
          <h2 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight text-royal-deep leading-none">
            Let&rsquo;s build something <span className="italic text-royal">timeless.</span>
          </h2>
          <p className="mt-6 text-muted-foreground lg:text-lg max-w-md">
            Share your project and our specification team will send curated samples,
            technical dossiers and pricing — usually within one working day.
          </p>
          <div className="mt-10 space-y-1 text-royal-deep">
            <a href="mailto:sales@fascinatedbyfloors.com" className="block font-display text-2xl hover:text-royal transition-colors duration-300" data-testid="contact-email-link">sales@fascinatedbyfloors.com</a>
            <a href="tel:+919266884224" className="block text-muted-foreground hover:text-royal transition-colors duration-300" data-testid="contact-phone-link">+91 92668 84224</a>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-7"
        >
          <QuoteFields form={form} set={set} />
          <button
            data-testid="lead-submit"
            onClick={() => submit()}
            disabled={loading}
            className="mt-10 group inline-flex items-center gap-2 bg-royal text-white text-sm font-medium pl-6 pr-5 py-3.5 rounded-full hover:bg-royal-dark transition-colors duration-300 disabled:opacity-60"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {loading ? "Sending..." : "Send request"}
            {!loading && <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />}
          </button>
        </motion.div>
      </div>
    </section>
  );
};
